import {
  REGISTER_SUCCESS,
  REGISTER_FAIL,
  LOGIN_SUCCESS,
  LOGIN_FAIL,
  LOGIN_MFA_REQUIRED,
  LOGOUT,
  LOGIN_INIT,
  USER_DETAILS_SUCCESS,
  USER_DETAILS_FAIL,
  USER_PROGRAMMES_SUCCESS,
  USER_PROGRAMMES_FAIL,
  USER_PROFILE_SUCCESS,
  USER_PROFILE_FAIL,
  CF_USER_DETAILS_FAIL,
  CF_USER_DETAILS_SUCCESS
} from './types';
import {
  API_REGISTER,
  API_LOGIN,
  API_MFA_VERIFY,
  API_MFA_RESEND,
  // API_LOGOUT,
  API_USER_DETAILS,
  API_CF_USER_DETAILS,
  API_USER_PROGRAMMES,
  API_USER_PROFILE
} from './constants';
import { setAlert } from './alert';
import axios from 'axios';
import api from '../util/api';

const config = {
  headers: {
    'Content-Type': 'application/json',
  },
  // withCredentials: true
};

// Register User
export const register = formData => async dispatch => {
  try {
    const { data } = await axios.post(
      process.env.REACT_APP_API_URL + API_REGISTER,
      formData,
      config
    );
    dispatch(setAlert('Registration Successful', 'success'));
    dispatch({
      type: REGISTER_SUCCESS,
      payload: data,
    });
  } catch (err) {
    dispatch(setAlert(err.response.data.detail, 'error'));
    dispatch({
      type: REGISTER_FAIL,
    });
  }
};

// Login User
// Trigger commit to sync recaptcha param
export const login =
  ({ username, email, password, recaptcha }) =>
    async dispatch => {
      const body = JSON.stringify({ username, email, password, recaptcha });
      try {
        dispatch({
          type: LOGIN_INIT,
        });
        const { data } = await api().post(
          process.env.REACT_APP_API_URL + API_LOGIN,
          body,
          config
        );
        if (data.mfa_required) {
          // No tokens yet: the caller shows the code screen for this challenge.
          dispatch({ type: LOGIN_MFA_REQUIRED });
          return {
            mfaRequired: true,
            challengeId: data.challenge_id,
            expiresIn: data.expires_in,
            resendAfter: data.resend_after,
          };
        }
        dispatch(setAlert('Logged In Successfully', 'success'));
        dispatch({
          type: LOGIN_SUCCESS,
          payload: data,
        });
        return { mfaRequired: false };
      } catch (err) {
        // console.log(err)
        dispatch(setAlert(authErrorMessage(err), 'error'));
        dispatch({
          type: LOGIN_FAIL
        });
        throw (err)
      }
    };

// Message for a failed auth request, including network errors and 5xx
// responses that have no JSON body.
const authErrorMessage = err =>
  err?.response?.data?.detail || 'Something went wrong. Please try again.';

// Second login step: exchange the emailed code for tokens. With
// rememberDevice the API sets an HttpOnly cookie so this browser can skip
// the code for 30 days. Rethrows so the caller can branch on
// err.response.data.code.
export const verifyMfa =
  ({ challengeId, code, rememberDevice }) =>
    async dispatch => {
      try {
        dispatch({ type: LOGIN_INIT });
        const { data } = await api().post(
          process.env.REACT_APP_API_URL + API_MFA_VERIFY,
          JSON.stringify({
            challenge_id: challengeId,
            code,
            remember_device: rememberDevice === true,
          }),
          config
        );
        dispatch(setAlert('Logged In Successfully', 'success'));
        dispatch({
          type: LOGIN_SUCCESS,
          payload: data,
        });
      } catch (err) {
        dispatch(setAlert(authErrorMessage(err), 'error'));
        dispatch({ type: LOGIN_MFA_REQUIRED });
        throw err;
      }
    };

// Email a new code for the current challenge. Returns the new resend wait.
export const resendMfa = challengeId => async dispatch => {
  try {
    const { data } = await api().post(
      process.env.REACT_APP_API_URL + API_MFA_RESEND,
      JSON.stringify({ challenge_id: challengeId }),
      config
    );
    dispatch(setAlert('A new code is on its way', 'success'));
    return { resendAfter: data.resend_after, expiresIn: data.expires_in };
  } catch (err) {
    dispatch(setAlert(authErrorMessage(err), 'error'));
    throw err;
  }
};

// Logout User
export const logout = (message = 'Logged Out Successfully', severity = 'success') => async dispatch => {
  dispatch(setAlert(message, severity));
  dispatch({
    type: LOGOUT,
  });
};

// Get Specific User Info
export const getUserProfile = () => async dispatch => {
  try {
    const { data } = await api().get(
      process.env.REACT_APP_API_URL + API_USER_PROFILE
    );
    dispatch({
      type: USER_PROFILE_SUCCESS,
      payload: data,
    });
  } catch (err) {
    dispatch(setAlert(err.response.data.detail, 'error'));
    dispatch({
      type: USER_PROFILE_FAIL,
    });
  }
};

// Get Additional User Data
export const getUserDetails = () => async dispatch => {
  try {
    const { data } = await api().get(
      process.env.REACT_APP_API_URL + API_USER_DETAILS
    );
    dispatch({
      type: USER_DETAILS_SUCCESS,
      payload: data,
    });
  } catch (err) {
    dispatch(setAlert(err.response.data.detail, 'error'));
    dispatch({
      type: USER_DETAILS_FAIL,
    });
    throw err;
  }
};

export const getCfUserDetails = () => async dispatch => {
  try {
    const { data } = await api().get(
      process.env.REACT_APP_API_URL + API_CF_USER_DETAILS
    );
    dispatch({
      type: CF_USER_DETAILS_SUCCESS,
      payload: data,
    });
  } catch (err) {
    dispatch(setAlert(err.response.data.detail, 'error'));
    dispatch({
      type: CF_USER_DETAILS_FAIL,
    });
  }
};

// Get User Programmes
export const getUserProgrammes = () => async dispatch => {
  try {
    const { data } = await api().get(
      process.env.REACT_APP_API_URL + API_USER_PROGRAMMES
    );
    dispatch({
      type: USER_PROGRAMMES_SUCCESS,
      payload: data,
    });
  } catch (err) {
    dispatch(setAlert(err.response.data.detail, 'error'));
    dispatch({
      type: USER_PROGRAMMES_FAIL,
    });
  }
};

// Add or Update User Info
export const addOrUpdateUserProfile = (info, type) => async dispatch => {
  try {
    const url = process.env.REACT_APP_API_URL + API_USER_PROFILE;
    const { data } =
      type === 'update'
        ? await api().put(url, info, config)
        : await api().post(url, info, config);
    dispatch(setAlert(data.detail, 'success'));
    dispatch({
      type: USER_PROFILE_SUCCESS,
      payload: data?.profileData,
    });
  } catch (err) {
    dispatch(setAlert(err.response.data.detail, 'error'));
    dispatch({
      type: USER_PROFILE_FAIL,
    });
  }
};